import React, { useState, useEffect, useCallback } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridFooter, GridToolbar } from "@mui/x-data-grid";
import {
  Grid,
  Box,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Typography,
  Divider,
  IconButton,
  Button,
  Alert,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { spacing } from "@mui/system";
import axios from "axios";
import ViewCustomerInvoiceDialog from "../../components/ViewCustomerInvoiceDialog";
import moment from "moment";
import SelectWrapper from "../../components/SelectBoxDataGridCell";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DataGridExport from "../../components/DataGridExport";
import ConfirmDialog from "../../components/ConfirmDialog";
import ViewClaimDialog from "../../components/ViewClaimDialog";
import PreFinanceCodeLink from "../../components/PreFinanceCodeLink";
import PreFinanceDossierDetailDialog from "../../components/PreFinanceDossierDetailDialog";
import { usePreFinanceDossierDialog } from "../../hooks/usePreFinanceDossierDialog";
import { formatPreFinanceDisplayCode } from "../../utils/preFinanceCodeUtils";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITIES } from "../../auth/permissions";

const Card = styled(MuiCard)(spacing);

const CardContent = styled(MuiCardContent)(spacing);

const AllInvoices = () => {
  const { hasPermission } = usePermissions();
  const canWriteInvoices = hasPermission(CAPABILITIES.invoicesWrite, "W");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = React.useState(15);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lookupsReady, setLookupsReady] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    customerInvoiceId: false,
    customerName: false,
    supplierCode: false,
  });
  const [supplierData, setSupplierData] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [invoiceStateData, setInvoiceStateData] = useState([]);
  const [invoiceLabelData, setInvoiceLabelData] = useState([]);
  const [productData, setProductData] = useState([]);
  const [currencyData, setCurrencyData] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("any");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedStartMonth, setSelectedStartMonth] = useState("any");
  const [selectedEndMonth, setSelectedEndMonth] = useState("any");
  const [selectedState, setSelectedState] = useState("any");
  const [appliedFilters, setAppliedFilters] = useState({
    country: "any",
    year: new Date().getFullYear(),
    startMonth: "any",
    endMonth: "any",
    state: "any",
  });
  const [tableData, setTableData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [openAlert, setOpenAlert] = React.useState(false);
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [alertMessage, setAlertMessage] = useState("");

  const [sortModel, setSortModel] = useState([{ field: "formattedInvoiceDate", sort: "desc" }]);

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [suppliers, countries, invoiceStates, products, currencies, invoiceLabels] = await Promise.all([
        axios.get("/api/v1/suppliers"),
        axios.get("/api/v1/countries"),
        axios.get("/api/v1/invoiceStates"),
        axios.get("/api/v1/productTypes"),
        axios.get("/api/v1/currencies"),
        axios.get("/api/v1/invoiceLabels"),
      ]);

      setSupplierData(suppliers.data);
      setCountryData(countries.data);
      setInvoiceStateData(invoiceStates.data);
      setProductData(products.data);
      setCurrencyData(currencies.data);
      setInvoiceLabelData(invoiceLabels.data);
      setLookupsReady(true);
    } catch (error) {
      console.error("Error fetching initial data", error);
      setLoading(false);
    }
  }, []);

  const fetchOverview = useCallback(async () => {
    if (!lookupsReady) return;

    setLoading(true);
    try {
      const params = {
        page: page + 1,
        pageSize,
        sortField: sortModel[0]?.field || "formattedInvoiceDate",
        sortDirection: sortModel[0]?.sort || "desc",
      };

      if (appliedFilters.country !== "any") {
        params.countryId = appliedFilters.country;
      }
      if (appliedFilters.year !== "any") {
        params.year = appliedFilters.year;
      }
      if (
        appliedFilters.year !== "any" &&
        appliedFilters.startMonth !== "any" &&
        appliedFilters.endMonth !== "any"
      ) {
        params.startMonth = appliedFilters.startMonth;
        params.endMonth = appliedFilters.endMonth;
      }
      if (appliedFilters.state !== "any") {
        params.invoiceStateId = appliedFilters.state;
      }

      const response = await axios.get("/api/v1/customerInvoices/overview", { params });
      const items = (response.data.items || []).map((invoice) => ({
        ...invoice,
        formattedInvoiceDate: invoice.invoiceDate
          ? moment(invoice.invoiceDate).format("DD-MM-YYYY")
          : "",
        formattedPreFinancedDate: invoice.preFinancePayoutDate
          ? moment(invoice.preFinancePayoutDate).format("DD-MM-YYYY")
          : "",
      }));

      setTableData(items);
      setRowCount(response.data.totalCount || 0);
    } catch (error) {
      console.error("Error fetching invoice overview", error);
      setOpenAlert(true);
      setAlertSeverity("error");
      setAlertMessage(error.response?.data?.message || "Error loading invoices");
    } finally {
      setLoading(false);
    }
  }, [lookupsReady, page, pageSize, sortModel, appliedFilters]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const handleFilter = () => {
    setPage(0);
    setAppliedFilters({
      country: selectedCountry,
      year: selectedYear,
      startMonth: selectedStartMonth,
      endMonth: selectedEndMonth,
      state: selectedState,
    });
  };

  const [viewClaimDialog, setViewClaimDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    custInfo: [],
  });

  const {
    selectedDossier,
    dialogContext,
    loadingPreFinanceId,
    error: preFinanceDialogError,
    setError: setPreFinanceDialogError,
    openDossierById,
    closeDossier,
    canFinalizePreFinance,
    canUnfinalizePreFinance,
  } = usePreFinanceDossierDialog();

  const columns = [
    {
      field: "customerInvoiceId",
      headerName: "id",
      flex: 0.5,
    },
    {
      field: "customerNumber",
      headerName: "Customer Number",
      flex: 0.5,
    },
    {
      field: "customerName",
      headerName: "Customer Name",
      flex: 0.7,
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice Number",
      flex: 0.38,
    },
    {
      field: "formattedInvoiceDate",
      headerName: "Invoice Date",
      flex: 0.35,
      type: "date",
      valueFormatter: (params) => params.value,
      sortComparator: (v1, v2) => moment(v1, "DD-MM-YYYY").toDate() - moment(v2, "DD-MM-YYYY").toDate(),
    },
    { field: "supplierName", headerName: "Supplier", flex: 0.8 },
    { field: "supplierCode", headerName: "Supplier Code", flex: 0.8 },
    {
      field: "countryCode",
      headerName: "Country",
      flex: 0.25,
      valueGetter: (params) => params.row.countryShortCode || "",
    },
    {
      field: "currencyCode",
      headerName: "Currency",
      flex: 0.25,
    },
    {
      field: "invoiceVATAmountLocalCurrency",
      headerName: "VAT Amount",
      flex: 0.4,
      align: "right",
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            paddingRight: "15px",
          }}
        >
          {params.value.toLocaleString("nl-NL", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      ),
    },
    {
      field: "invoiceStateShortCode",
      headerName: "Invoice State",
      flex: 0.33,
    },
    {
      field: "claimStateLabel",
      headerName: "Claim State",
      flex: 0.33,
      renderCell: (params) => {
        if (params.value) {
          return (
            <Button
              variant="text"
              onClick={() => {
                setViewClaimDialog({
                  isOpen: true,
                  title: "View Claim",
                  subTitle: "Details for Claim " + params.row.claimId,
                  params: params.row,
                  custInfo: [{ customerId: params.row.customerId, customerNumber: params.row.customerNumber }],
                });
              }}
            >
              {params.value}
            </Button>
          );
        }
        return "";
      },
    },
    {
      field: "claimCode",
      headerName: "Claim Code",
      flex: 0.58,
      renderCell: (params) => {
        if (!params.value) {
          return "";
        }
        return (
          <Button
            variant="text"
            onClick={() => {
              setViewClaimDialog({
                isOpen: true,
                title: "View Claim",
                subTitle: "Details for Claim " + params.row.claimId,
                params: params.row,
                custInfo: [{ customerId: params.row.customerId, customerNumber: params.row.customerNumber }],
              });
            }}
          >
            {String(params.value)}
          </Button>
        );
      },
    },
    {
      field: "preFinanceCode",
      headerName: "Pre-Finance Code",
      flex: 0.7,
      minWidth: 190,
      valueGetter: (params) =>
        params.row.preFinanceId > 0
          ? formatPreFinanceDisplayCode({
              preFinanceId: params.row.preFinanceId,
              preFinanceCode: params.row.preFinanceCode,
              isLegacy: params.row.preFinanceIsLegacy,
            })
          : "",
      renderCell: (params) => (
        <PreFinanceCodeLink
          preFinanceId={params.row.preFinanceId}
          preFinanceCode={params.row.preFinanceCode}
          isLegacy={params.row.preFinanceIsLegacy}
          onOpen={(preFinanceId) =>
            openDossierById(preFinanceId, {
              customerId: params.row.customerId,
              customerNumber: params.row.customerNumber,
            })
          }
          isLoading={loadingPreFinanceId === params.row.preFinanceId}
        />
      ),
    },
    {
      field: "formattedPreFinancedDate",
      headerName: "Pre-Fin Date",
      flex: 0.4,
      type: "date",
      valueFormatter: (params) => params.value,
      sortComparator: (v1, v2) => {
        if (!v1 && !v2) return 0;
        if (!v1) return 1;
        if (!v2) return -1;
        return moment(v1, "DD-MM-YYYY").toDate() - moment(v2, "DD-MM-YYYY").toDate();
      },
    },

    {
      field: "fileId",
      headerName: "PDF",
      flex: 0.2,
      renderCell: (params) =>
        params.value > 0 ? (
          <CheckCircleOutlineIcon style={{ color: "green", margin: "0 auto" }} />
        ) : (
          <CheckCircleOutlineIcon style={{ color: "red", margin: "0 auto" }} />
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.35,
      renderCell: (params) => {
        return (
          <Box>
            <IconButton
              disabled={!canWriteInvoices}
              aria-label="Delete invoice"
              onClick={(e) => {
                setConfirmDialog({
                  isOpen: true,
                  title: "Delete Invoice",
                  subTitle: "Are you sure you want to delete invoice " + params.row.invoiceNumber + "?",
                  onConfirm: () => {
                    onDeleteConfirmed(e, params.row);
                  },
                });
              }}
            >
              <DeleteIcon />
            </IconButton>
            <IconButton
              onClick={(e) => {
                setViewCustomerInvoiceDialog({
                  isOpen: true,
                  downloadFile: true,
                  title: "Manage Invoice",
                  params: params.row,
                  custInfo: [{ customerId: params.row.customerId, customerNumber: params.row.customerNumber }],
                  productInfo: [
                    {
                      productCode: params.row.claimProductCode,
                      productSubCode: params.row.claimProductSubCode,
                    },
                  ],
                  subTitle: "Are you sure you want to manage " + params.row.contactName + "?",
                  onConfirm: () => {
                    onEditConfirmed(e, params.row);
                  },
                  handleFile: () => {
                    handleFile();
                  },
                });
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleFile = () => {};

  // const fetchCustomerInvoices = useCallback(async () => {
  //   try {
  //     const response = await axios.get("/api/v1/customerInvoicesWithProductCodes");
  //     setTableData(response.data);
  //   } catch (error) {
  //     console.error("Error fetching customer invoices by entity ID:", error);
  //   }
  // }, []);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {},
  });

  const [viewCustomerInvoiceDialog, setViewCustomerInvoiceDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    custInfo: [],
    productInfo: [],
  });

  function onEditConfirmed(e, params) {
    window.location.reload(false);
    setViewCustomerInvoiceDialog({
      ...viewCustomerInvoiceDialog,
      isOpen: false,
    });
  }

  const handleEditCellChange = (params) => {
    const updatedRows = tableData.map((row) => (row.id === params.id ? { ...row, [params.field]: params.props.value } : row));
    setTableData(updatedRows);
  };

  const onDeleteConfirmed = async (e, params) => {
    try {
      await axios.delete("/api/v1/customerInvoices/" + params.customerInvoiceId);
      
      fetchOverview();
      
      // Show success message
      setOpenAlert(true);
      setAlertSeverity("success");
      setAlertMessage("Invoice " + params.invoiceNumber + " deleted successfully!");
      setTimeout(() => {
        setOpenAlert(false);
      }, 5000);
      
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    } catch (error) {
      // Show error message
      setOpenAlert(true);
      setAlertSeverity("error");
      setAlertMessage(error.response?.data?.message || "Error deleting invoice");
      setTimeout(() => {
        setOpenAlert(false);
      }, 5000);
      
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  function generateArrayOfYears() {
    var max = new Date().getFullYear() + 1;
    var min = 2021;
    var years = [];
    for (var i = max; i >= min; i--) {
      years.push({ yearId: i, yearText: i });
    }

    return years;
  }

  let yearArr = generateArrayOfYears().reverse();

  const monthArr = [
    { monthId: 1, monthText: "Jan" },
    { monthId: 2, monthText: "Feb" },
    { monthId: 3, monthText: "Mar" },
    { monthId: 4, monthText: "Apr" },
    { monthId: 5, monthText: "May" },
    { monthId: 6, monthText: "Jun" },
    { monthId: 7, monthText: "Jul" },
    { monthId: 8, monthText: "Aug" },
    { monthId: 9, monthText: "Sep" },
    { monthId: 10, monthText: "Oct" },
    { monthId: 11, monthText: "Nov" },
    { monthId: 12, monthText: "Dec" },
  ];

  const handleCountryValue = (value, name) => {
    setSelectedCountry(value);
  };
  const handleYearValue = (value, name) => {
    setSelectedYear(value);
  };
  const handleStartMonthValue = (value, name) => {
    setSelectedStartMonth(value);
  };
  const handleEndMonthValue = (value, name) => {
    setSelectedEndMonth(value);
  };
  const handleStateValue = (value) => {
    setSelectedState(value);
  };

  const handleResetFilter = () => {
    const currentYear = new Date().getFullYear();
    setSelectedCountry("any");
    setSelectedYear(currentYear);
    setSelectedStartMonth("any");
    setSelectedEndMonth("any");
    setSelectedState("any");
    setPage(0);
    setAppliedFilters({
      country: "any",
      year: currentYear,
      startMonth: "any",
      endMonth: "any",
      state: "any",
    });
  };

  const handleSelectionChange = (newSelection) => {
    setSelectedRows(newSelection);
    // let selectedRowsAmount = 0;
    // let selectedRowsVATAmount = 0;
    // // let disablePreFinanced = false;

    // newSelection.forEach((rowId) => {
    //   const selectedRow = tableData.find((row) => row.customerInvoiceId === rowId);
    //   if (selectedRow) {
    //     selectedRowsAmount += selectedRow.invoiceGrossAmount;
    //     selectedRowsVATAmount += selectedRow.invoiceVATAmountLocalCurrency;

    //     // Check if preFinancedId is greater than 0
    //     if (selectedRow.preFinanceId > 0) {
    //       // disablePreFinanced = true;
    //     }
    //   }
    // });

    // setSelectedInvoiceCount(newSelection.length);
    // setSelectedInvoiceTotalAmount(selectedRowsAmount);
    // setSelectedInvoiceTotalVATAmount(selectedRowsVATAmount);

    // Update the button's disabled state
    // setIsPreFinancedDisabled(disablePreFinanced);
  };

  const handleSetState = () => {
    if (selectedRows.length <= 0) {
      setOpenAlert(true);
      setAlertSeverity("error");
      setAlertMessage("No invoices selected");
      setTimeout(() => {
        setOpenAlert(false);
      }, 5000);
      return;
    }

    // Show the custom ConfirmDialog
    setConfirmDialog({
      isOpen: true,
      title: "Confirm Set State",
      subTitle: "Are you sure you want to set selected invoices to state 'RFC'?",
      onConfirm: () => {
        // Set state Id to 3 (RFC)
        const invoiceStateId = 3;

        let rowsToBeUpdated = [];
        selectedRows.forEach((rowId) => {
          const selectedRow = {};

          selectedRow.customerInvoiceId = rowId;
          selectedRow.invoiceStateId = invoiceStateId;

          if (selectedRow) {
            rowsToBeUpdated.push(selectedRow);
          }
        });
        const jsonRowsToBeUpdated = JSON.stringify(rowsToBeUpdated);
        axios
          .put("/api/v1/customerInvoices/updateInvoiceStateId", jsonRowsToBeUpdated, {
            headers: {
              "Content-Type": "application/json",
            },
          })
          .then((response) => {
            if (response.status !== 204) {
              // Replace 200 with the actual status code your API returns upon successful update
              throw new Error("Failed to update invoice states");
            }
            // console.table(response.data);
            fetchOverview();
            setSelectedRows([]);
            setOpenAlert(true);
            setAlertSeverity("success");
            setAlertMessage("Invoice states are updated!");
            setTimeout(() => {
              setOpenAlert(false);
            }, 5000);
            setConfirmDialog({ ...confirmDialog, isOpen: false });
          })
          .catch((error) => {
            setOpenAlert(true);
            setAlertSeverity("error");
            setAlertMessage(error.message);
            setTimeout(() => {
              setOpenAlert(false);
            }, 5000);
            setConfirmDialog({ ...confirmDialog, isOpen: false });
          });
      },
      onCancel: () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      },
    });
  };

  // const [selectedRows, setSelectedRows] = useState([]);
  // const [selectedInvoiceCount, setSelectedInvoiceCount] = useState(0);
  // const [selectedInvoiceTotalAmount, setSelectedInvoiceTotalAmount] = useState(0);
  // const [selectedInvoiceTotalVATAmount, setSelectedInvoiceTotalVATAmount] = useState(0);
  // const [isPreFinancedDisabled, setIsPreFinancedDisabled] = useState(false);

  // const handleSelectionChange = (newSelection) => {
  //   setSelectedRows(newSelection);
  //   let selectedRowsAmount = 0;
  //   let selectedRowsVATAmount = 0;
  //   let disablePreFinanced = false;

  //   newSelection.forEach((rowId) => {
  //     const selectedRow = tableData.find((row) => row.customerInvoiceId === rowId);
  //     if (selectedRow) {
  //       selectedRowsAmount += selectedRow.invoiceGrossAmount;
  //       selectedRowsVATAmount += selectedRow.invoiceVATAmountLocalCurrency;

  //       if (selectedRow.preFinanceId > 0) {
  //         disablePreFinanced = true;
  //       }
  //     }
  //   });

  //   setSelectedInvoiceCount(newSelection.length);
  //   setSelectedInvoiceTotalAmount(selectedRowsAmount);
  //   setSelectedInvoiceTotalVATAmount(selectedRowsVATAmount);

  //   setIsPreFinancedDisabled(disablePreFinanced);
  // };

  const BulkEditFooter = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          borderTop: "1px solid #ccc",
          paddingTop: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start" }}></div>
        <div>
          <div>
            <GridFooter
              style={{ borderTop: "none" }}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[15, 50, 100]}
            />
          </div>
        </div>
      </div>
    );
  };

  // const [openAlert, setOpenAlert] = React.useState(false);
  // const [alertSeverity, setAlertSeverity] = useState("info");
  // const [alertMessage, setAlertMessage] = useState("");

  return (
    <React.Fragment>
      <ViewClaimDialog viewClaimDialog={viewClaimDialog} setViewClaimDialog={setViewClaimDialog} />
      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
      <PreFinanceDossierDetailDialog
        dossier={selectedDossier}
        open={Boolean(selectedDossier)}
        onClose={closeDossier}
        customerId={dialogContext.customerId ?? selectedDossier?.entityId}
        customerNumber={dialogContext.customerNumber}
        canFinalizePreFinance={canFinalizePreFinance}
        canUnfinalizePreFinance={canUnfinalizePreFinance}
      />

      <Snackbar
        open={Boolean(preFinanceDialogError)}
        autoHideDuration={6000}
        onClose={() => setPreFinanceDialogError("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setPreFinanceDialogError("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {preFinanceDialogError}
        </Alert>
      </Snackbar>

      <Snackbar
        open={openAlert}
        autoHideDuration={5000}
        onClose={() => setOpenAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpenAlert(false)}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      <Helmet title="All" />

      <Divider my={6} />
      <Grid item xs={12} md={6}></Grid>
      <Card mb={0}>
        <CardContent
          pb={1}
          sx={{
            marginBottom: 0,
          }}
        ></CardContent>
        <Grid container spacing={0} mt={0}>
          <Grid container xs={12} spacing={5} sx={{ ml: 1, pr: 2 }}>
            <Grid item xs={2}>
              <SelectWrapper
                name="countryId"
                labelColor="#f64435"
                label="Country"
                options={[
                  { key: "any", value: "Any" },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(countryData).split('"countryId":').join('"key":')))
                      .split('"countryCode":')
                      .join('"value":')
                  ),
                ]}
                value={selectedCountry}
                handleChange={handleCountryValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="year"
                label="Year"
                options={[
                  { key: "any", value: "Any" },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(yearArr).split('"yearId":').join('"key":')))
                      .split('"yearText":')
                      .join('"value":')
                  ),
                ]}
                value={selectedYear}
                handleChange={handleYearValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="startMonth"
                label="Start month"
                options={[
                  { key: "any", value: "Any" },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(monthArr).split('"monthId":').join('"key":')))
                      .split('"monthText":')
                      .join('"value":')
                  ),
                ]}
                value={selectedStartMonth}
                handleChange={handleStartMonthValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="endMonth"
                label="End month"
                options={[
                  { key: "any", value: "Any" },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(monthArr).split('"monthId":').join('"key":')))
                      .split('"monthText":')
                      .join('"value":')
                  ),
                ]}
                value={selectedEndMonth}
                handleChange={handleEndMonthValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="invoiceStateId"
                label="Invoice State"
                options={[
                  { key: "any", value: "Any" },
                  ...invoiceStateData.map((state) => ({
                    key: state.invoiceStateId,
                    value: state.invoiceStateShortCode,
                  })),
                ]}
                value={selectedState}
                handleChange={handleStateValue}
              />
            </Grid>
            <Grid item xs={2}>
              <Button onClick={handleFilter} sx={{ mt: 4, pl: 6, pr: 6 }} variant="contained">
                Filter
              </Button>
              <Button onClick={handleResetFilter} sx={{ mt: 4, ml: 4, pl: 6, pr: 6 }} variant="outlined">
                Reset
              </Button>
            </Grid>
          </Grid>
          {viewCustomerInvoiceDialog.isOpen && (
            <ViewCustomerInvoiceDialog
              key={viewCustomerInvoiceDialog.params?.customerInvoiceId || Date.now()}
              viewCustomerInvoiceDialog={viewCustomerInvoiceDialog}
              setViewCustomerInvoiceDialog={setViewCustomerInvoiceDialog}
              countryArr={countryData}
              supplierArr={supplierData}
              currencyArr={currencyData}
              productTypeArr={productData}
              invoiceStateArr={invoiceStateData}
              invoiceLabelArr={invoiceLabelData}
              refreshGrid={fetchOverview}
            />
          )}
          <Grid item xs={12}>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <DataGridExport tableData={tableData} columns={columns} />
            </div>
            {!lookupsReady && loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "600px",
                }}
              >
                <Typography sx={{ mr: 6, fontSize: 20 }}>Loading</Typography>
                <CircularProgress />
              </div>
            ) : (
              <DataGrid
                checkboxSelection
                disableSelectionOnClick
                idProperty="customerInvoiceId"
                editMode="cell"
                getRowId={(row) => row.customerInvoiceId}
                onEditCellChange={handleEditCellChange}
                selectionModel={selectedRows}
                onSelectionModelChange={handleSelectionChange}
                components={{ Toolbar: GridToolbar, Footer: BulkEditFooter }}
                density="compact"
                loading={loading}
                componentsProps={{
                  toolbar: {
                    csvOptions: { disableToolbarButton: false },
                    printOptions: { disableToolbarButton: false },
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 250 },
                  },
                }}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                rowIndexColumn
                experimentalFeatures={{ newEditingApi: true }}
                rows={tableData}
                columns={columns}
                pagination
                paginationMode="server"
                sortingMode="server"
                rowCount={rowCount}
                page={page}
                onPageChange={(newPage) => setPage(newPage)}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => {
                  setPage(0);
                  setPageSize(newPageSize);
                }}
                rowsPerPageOptions={[15, 50, 100]}
                sortingOrder={["desc", "asc"]}
                sx={{
                  height: 677,
                  width: "100%",
                  borderRadius: 1,
                  backgroundColor: "background.paper",
                  boxShadow: 2,
                  "& .MuiDataGrid-cell:hover": {
                    color: "primary.main",
                  },
                  marginTop: 0,
                }}
                sortModel={sortModel}
                onSortModelChange={(model) => {
                  setPage(0);
                  setSortModel(model.length ? model : [{ field: "formattedInvoiceDate", sort: "desc" }]);
                }}
              />
            )}
            <div style={{ alignSelf: "flex-end", marginBottom: 5, marginTop: 10 }}>
              {/* <Button variant="contained" color="primary" onClick={handlePreFinanced} disabled={isPreFinancedDisabled} sx={{ mr: 3 }}>
                Set selected to pre-financed
              </Button> */}

              <Button
                variant="contained"
                color="success"
                onClick={handleSetState}
                sx={{ m: 3 }}
                // disabled={isPreFinancedDisabled}
              >
                Set Invoice State to RFC
              </Button>
            </div>
          </Grid>
        </Grid>
      </Card>
    </React.Fragment>
  );
};

export default AllInvoices;
